import { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, FormControl, FormSelect, Row, Stack } from "react-bootstrap";
import { FareZone } from "../../types/FareZone";
import {  useFormikContext } from "formik";
import { AddFareZone } from "../../types/AddFareZone";
import { EditFareZone } from "../../types/EditFareZone";
import { useAlertContext } from "../alert/AlertContext";
import StationService from "../../services/StationService";
import { FareZoneStation } from "../../types/FareZoneStation";
import SearchableMultiSelect from "../searchableMutliSelect/SearchableMultiSelect";


export interface FareZoneFormSchemesTabProps {
    currentFareZone: FareZone | undefined;
    reviewMode: boolean;
  }


const FareZoneFormSchemesTab = ({currentFareZone, reviewMode}:FareZoneFormSchemesTabProps ) => {
    const { values, setValues ,handleBlur, handleChange, setFieldValue } =
    useFormikContext<AddFareZone | EditFareZone>();

    useEffect(() => {
        if(!values.schemes) {
            addScheme();
        }
    }, []);

    const [selectedIndex, setSelectedIndex] = useState<number | undefined>();
    const [selectedValue, setSelectedValue] = useState<number | string>("");
    const [isAddingScheme, setIsAddingScheme] = useState<boolean>(false);
   
    const alertContext = useAlertContext();
    const stationService = useMemo(
      () => new StationService(alertContext.showAlert),
      [alertContext],
    );

    const getStations = async (filter: string) => {
        return stationService.getAll(filter, values.boundary).then((stations) =>
          stations.map(
            (m) =>
              ({
                nlc: m.nlc,
                name: m.name,
                psCode: "",
                ticketName: `${m.name.toLocaleUpperCase()}+BUS`,
              } as FareZoneStation),
          ),
        );
      };

    const onSchemeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if(isAddingScheme) cancelAddScheme();
        
        const index = parseInt(event.target.value);
        setSelectedValue(index);
        setSelectedIndex(index);
    };

    const addScheme = () => {
        setIsAddingScheme(true);
        const newScheme = {
            psCode: "",
            schemeName: "",
            ticketName: "",
            pbUrl: "",
            exclusions: "",
            stations: [] as FareZoneStation[]
        };
    const currentValues = values;
    const newSchemes = [...currentValues.schemes ?? [], newScheme];
    setValues({ ...currentValues, schemes: newSchemes } as EditFareZone | AddFareZone);
    setSelectedIndex(newSchemes.length - 1);
    setSelectedValue("");
    }

    const cancelAddScheme = () => {
        setIsAddingScheme(false);
        const currentValues = values;
        const newSchemes = currentValues.schemes.filter((x, index) => index !== selectedIndex);
        setValues({ ...currentValues, schemes: newSchemes } as EditFareZone | AddFareZone);
        setSelectedIndex(undefined);
        setSelectedValue("");
    }

    return(<>
        
        {
            currentFareZone && currentFareZone.schemes.length > 0 && (
        <Row>
            <Col md={6} sm={12}>
        <FormSelect
                value={selectedValue}
                onChange={onSchemeChange}
                disabled={reviewMode}>
                <option value={""} disabled>
                  Select a scheme
                </option>
                {currentFareZone?.schemes.map((scheme,index) => (
                  <option key={index} value={index}>
                    {scheme.schemeName }
                  </option>
                ))}
              </FormSelect>
              
        </Col>
        <Col md={6} sm={12}>
            {
                !isAddingScheme && (
                    <Button onClick={addScheme}>Add Scheme</Button>
                )
            }
            {
                isAddingScheme && (
                    <Button variant="danger" onClick={() => cancelAddScheme()}>Cancel</Button>
                )
            }
        </Col>
        </Row>
            )
        }
        {
                selectedIndex != null && (
                    <Stack className="mt-1" gap={1}>
                    <Form.Group>
                    <Form.Label htmlFor={`schemes[${selectedIndex}].psCode`}>PS Code</Form.Label>
                    <FormControl
                      type="text"
                      name={`schemes[${selectedIndex}].psCode`}
                      id={`schemes[${selectedIndex}].psCode`}
                      value={values.schemes[selectedIndex].psCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={reviewMode}
                      readOnly={reviewMode}
                    />
                  </Form.Group>
                    <Form.Group>
                    <Form.Label htmlFor={`schemes[${selectedIndex}].schemeName`}>Name</Form.Label>
                    <FormControl
                      type="text"
                      name={`schemes[${selectedIndex}].schemeName`}
                      id={`schemes[${selectedIndex}].schemeName`}
                      value={values.schemes[selectedIndex].schemeName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={reviewMode}
                      readOnly={reviewMode}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label htmlFor={`schemes[${selectedIndex}].ticketName`}>Ticket Name</Form.Label>
                    <FormControl
                      type="text"
                      name={`schemes[${selectedIndex}].ticketName`}
                      id={`schemes[${selectedIndex}].ticketName`}
                      value={values.schemes[selectedIndex].ticketName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={reviewMode}
                      readOnly={reviewMode}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label htmlFor={`schemes[${selectedIndex}].pbUrl`}>PlusBus Url</Form.Label>
                    <FormControl
                      type="text"
                      name={`schemes[${selectedIndex}].pbUrl`}
                      id={`schemes[${selectedIndex}].pbUrl`}
                      value={values.schemes[selectedIndex].pbUrl}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={reviewMode}
                      readOnly={reviewMode}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label htmlFor={`schemes[${selectedIndex}].exclusions`}>Exclusions</Form.Label>
                    <FormControl
                      as="textarea"
                      name={`schemes[${selectedIndex}].exclusions`}
                      id={`schemes[${selectedIndex}].exclusions`}
                      value={values.schemes[selectedIndex].exclusions}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={reviewMode}
                      readOnly={reviewMode}
                    />
                  </Form.Group>
                  <Form.Label>Stations</Form.Label>
                  <SearchableMultiSelect
                    id={`schemes[${selectedIndex}].stations`}
                    displayProperty="name"
                    value={values?.schemes[selectedIndex].stations ?? []}
                    dataCallback={getStations}
                    onChange={(value) => setFieldValue(`schemes[${selectedIndex}].stations`, value)}
                    disabled={reviewMode}
                    readOnly={reviewMode}
                    />
                  </Stack>
                )
            }
    </>);
}

export default FareZoneFormSchemesTab;